// src/pages/Dashboard/Admin/PagesPersonnalisees/hooks/usePagesDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createPage,
  deletePage,
  getAllPages,
  updatePage,
} from "../../../../../services/pagesPersonnaliseesServices";

import {
  DEFAULT_PAGE_FILTERS,
  buildPagePayload,
  calculatePagesStats,
  filterPages,
  safeArray,
  sortPages,
} from "../utils/pagesDashboardUtils";

export function usePagesDashboard() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_PAGE_FILTERS);

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editablePage, setEditablePage] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);

  const [articlesDrawerOpen, setArticlesDrawerOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);

  const [deletingId, setDeletingId] = useState(null);

  const fetchPages = useCallback(async () => {
    try {
      setLoading(true);

      const result = await getAllPages();
      setPages(sortPages(safeArray(result), filters.sort));
    } catch (error) {
      console.error("Erreur chargement pages :", error);
      toast.error("Erreur lors du chargement des pages.");
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [filters.sort]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const filteredPages = useMemo(() => {
    return filterPages(pages, filters);
  }, [pages, filters]);

  const stats = useMemo(() => calculatePagesStats(pages), [pages]);

  const filteredStats = useMemo(
    () => calculatePagesStats(filteredPages),
    [filteredPages]
  );

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_PAGE_FILTERS);
  };

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditablePage(null);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (page) => {
    setFormMode("edit");
    setEditablePage(page || null);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setFormMode("create");
    setEditablePage(null);
  };

  const handleCreatePage = async (formData) => {
    try {
      setSubmittingForm(true);

      await createPage(buildPagePayload(formData));

      toast.success("Page créée avec succès.");
      await fetchPages();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur création page :", error);
      toast.error("Erreur lors de la création de la page.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleUpdatePage = async (formData) => {
    if (!editablePage?.id) {
      toast.error("Page introuvable.");
      return;
    }

    try {
      setSubmittingForm(true);

      await updatePage(buildPagePayload(formData, editablePage.id));

      toast.success("Page mise à jour avec succès.");
      await fetchPages();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur mise à jour page :", error);
      toast.error("Erreur lors de la mise à jour de la page.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleDeletePage = async (page) => {
    if (!page?.id) return;

    const label = page?.titre || page?.title || "cette page";

    if (!window.confirm(`Supprimer définitivement « ${label} » ?`)) {
      return;
    }

    try {
      setDeletingId(page.id);

      const ok = await deletePage(page.id);

      if (ok === false) {
        toast.error("Erreur lors de la suppression de la page.");
        return;
      }

      toast.success("Page supprimée avec succès.");
      setPages((prev) => prev.filter((item) => item.id !== page.id));
    } catch (error) {
      console.error("Erreur suppression page :", error);
      toast.error("Erreur lors de la suppression de la page.");
    } finally {
      setDeletingId(null);
    }
  };

  const openArticlesDrawer = (page) => {
    setSelectedPage(page || null);
    setArticlesDrawerOpen(true);
  };

  const closeArticlesDrawer = () => {
    setArticlesDrawerOpen(false);
    setSelectedPage(null);
  };

  return {
    pages,
    filteredPages,
    loading,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,

    formDrawerOpen,
    formMode,
    editablePage,
    submittingForm,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleCreatePage,
    handleUpdatePage,

    articlesDrawerOpen,
    selectedPage,
    openArticlesDrawer,
    closeArticlesDrawer,

    deletingId,
    handleDeletePage,

    fetchPages,
  };
}